import { formatCedis } from '@accra/shared';

export interface CardData {
  score: number;
  newBest: boolean;
  routeName: string;
  vehicleName: string;
  metres: number;
  passengersDropped: number;
  /** Fares minus fines and repairs, pesewas. */
  netPesewas: number;
  /** "Daily challenge #3" / "Beat Kofi!" and so on, shown under the score. */
  headline: string;
  /** Badge icons earned this shift. */
  badges: string[];
}

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;

/** The three numbers along the bottom of the card. */
export function cardStats(data: CardData): { label: string; value: string }[] {
  return [
    { label: 'DISTANCE', value: `${(data.metres / 1000).toFixed(1)} km` },
    { label: 'PASSENGERS', value: String(data.passengersDropped) },
    { label: data.netPesewas < 0 ? 'LOSS' : 'EARNED', value: formatCedis(Math.abs(data.netPesewas)) },
  ];
}

/** Scales an image to cover a box, centred. */
function cover(ctx: CanvasRenderingContext2D, image: CanvasImageSource & { width: number; height: number }, x: number, y: number, w: number, h: number): void {
  const scale = Math.max(w / image.width, h / image.height);
  const iw = image.width * scale;
  const ih = image.height * scale;
  ctx.drawImage(image, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

type Scene = CanvasImageSource & { width: number; height: number };
const W = CARD_WIDTH;
const H = CARD_HEIGHT;

function drawBackdrop(ctx: CanvasRenderingContext2D, scene: Scene | null): void {
  ctx.fillStyle = '#11131c';
  ctx.fillRect(0, 0, W, H);
  if (scene) cover(ctx, scene, 0, 0, W, 820);
  const fade = ctx.createLinearGradient(0, 120, 0, 900);
  fade.addColorStop(0, 'rgba(17,19,28,0.55)');
  fade.addColorStop(0.55, 'rgba(17,19,28,0.35)');
  fade.addColorStop(1, '#11131c');
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, W, 900);
  for (const [i, colour] of ['#ce1126', '#fcd116', '#006b3f'].entries()) {
    ctx.fillStyle = colour;
    ctx.fillRect((W / 3) * i, 0, W / 3, 20);
  }
}

function drawTitle(ctx: CanvasRenderingContext2D): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = '84px "Bowlby One", Impact, sans-serif';
  ctx.lineWidth = 12;
  ctx.strokeStyle = '#ce1126';
  ctx.strokeText('TROTRO RUN', W / 2, 150);
  ctx.fillStyle = '#fcd116';
  ctx.fillText('TROTRO RUN', W / 2, 150);
  ctx.font = '700 30px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#fdf8ec';
  ctx.letterSpacing = '6px';
  ctx.fillText('A DRIVING GAME SET IN ACCRA', W / 2, 200);
  ctx.letterSpacing = '0px';
}

function drawScore(ctx: CanvasRenderingContext2D, data: CardData): void {
  ctx.font = '230px "Bowlby One", Impact, sans-serif';
  ctx.lineWidth = 16;
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.strokeText(data.score.toLocaleString(), W / 2, 560);
  ctx.fillStyle = '#fcd116';
  ctx.fillText(data.score.toLocaleString(), W / 2, 560);
  ctx.font = '800 38px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#fdf8ec';
  ctx.fillText(data.newBest ? 'POINTS · NEW BEST!' : 'POINTS', W / 2, 622);
  if (data.headline) {
    ctx.font = '800 44px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#3ddc84';
    ctx.fillText(data.headline, W / 2, 700);
  }
  ctx.font = '700 34px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#c9c2b0';
  ctx.fillText(`${data.routeName} · ${data.vehicleName}`, W / 2, data.headline ? 760 : 710);
}

function drawStats(ctx: CanvasRenderingContext2D, data: CardData): void {
  const boxW = 300;
  const gap = 30;
  const left = (W - (boxW * 3 + gap * 2)) / 2;
  cardStats(data).forEach((stat, i) => {
    const x = left + i * (boxW + gap);
    ctx.fillStyle = 'rgba(34,38,58,0.95)';
    roundedRect(ctx, x, 880, boxW, 170, 24);
    ctx.fill();
    ctx.fillStyle = '#c9c2b0';
    ctx.font = '800 26px Inter, system-ui, sans-serif';
    ctx.fillText(stat.label, x + boxW / 2, 930);
    ctx.fillStyle = '#ffffff';
    // Long figures (GH₵1,432.00) shrink to fit their box.
    for (let size = 52; size >= 30; size -= 2) {
      ctx.font = `${size}px "Bowlby One", Impact, sans-serif`;
      if (ctx.measureText(stat.value).width < boxW - 36) break;
    }
    ctx.fillText(stat.value, x + boxW / 2, 1006);
  });
  if (data.badges.length > 0) {
    ctx.font = '64px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
    ctx.fillText(data.badges.slice(0, 5).join(' '), W / 2, 1130);
  }
}

function drawFooter(ctx: CanvasRenderingContext2D, url: string): void {
  ctx.fillStyle = '#fdf8ec';
  ctx.font = '800 44px Inter, system-ui, sans-serif';
  ctx.fillText('Think you can beat me?', W / 2, 1195);
  const label = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  ctx.font = '800 40px Inter, system-ui, sans-serif';
  const pill = Math.min(W - 120, ctx.measureText(label).width + 90);
  ctx.fillStyle = '#fcd116';
  roundedRect(ctx, (W - pill) / 2, 1228, pill, 80, 40);
  ctx.fill();
  ctx.fillStyle = '#11131c';
  ctx.fillText(label, W / 2, 1282);
}

/**
 * Draws the shareable picture (1080×1350, the shape WhatsApp and Instagram like): a photo of your road from
 * the game, the score big in the middle, three numbers and the game's name and link. `scene` is a snapshot of the game.
 */
export function drawScoreCard(data: CardData, scene: Scene | null, url: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  drawBackdrop(ctx, scene);
  drawTitle(ctx);
  drawScore(ctx, data);
  drawStats(ctx, data);
  drawFooter(ctx, url);
  return canvas;
}

/** Sends the picture to the phone's share sheet (WhatsApp and so on); on a computer it downloads it. */
export async function shareCardImage(canvas: HTMLCanvasElement, text: string, url: string): Promise<'shared' | 'saved' | 'failed'> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return 'failed';
  const file = new File([blob], 'trotro-run-score.png', { type: 'image/png' });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: `${text} ${url}` });
      return 'shared';
    } catch (error) {
      // Closing the share sheet is not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') return 'shared';
    }
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = file.name;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 4000);
  return 'saved';
}
